import { formatNumber } from '@angular/common'
import { TierType } from '@lla-platform/core/core-data-access'
import { TargetType, TargetTypeSign } from '../enums/target-type.enum'

// Tier Progress (GLOSSARY.md), client-side copy of the API's TierProgress value object. Used only for
// Rollup Rows (Summary / State / Region / District / Group, WoW summary); location rows come from the API.

/** Percent Precision (GLOSSARY.md) on the Targets pages. */
export const TARGETS_PERCENT_DECIMALS = 2
/** Percent Precision (GLOSSARY.md) on Week over Week. */
export const WOW_PERCENT_DECIMALS = 1

/** Display Precision (GLOSSARY.md): whole units for money and counts, two decimals for Gross Profit. */
export const displayDecimals = (objective: TargetType): number =>
  objective === TargetType.GrossProfit ? 2 : 0

/** Midpoint away from zero, like the API's Round extension (not JS Math.round, which rounds half toward +∞). */
export const roundAwayFromZero = (value: number, decimals: number): number => {
  const factor = 10 ** decimals
  return (Math.sign(value) * Math.round(Math.abs(value) * factor)) / factor
}

/** Mirrors the API's GetValue(targetType, 0): $ and whole units for money, whole units for counts, 2 dp + % for Gross Profit. */
export const formatObjectiveValue = (value: number, objective: TargetType): string => {
  const decimals = displayDecimals(objective)
  const text = formatNumber(roundAwayFromZero(value, decimals), 'en-US', `1.${decimals}-${decimals}`)
  const sign = TargetTypeSign[objective] ?? ''
  return sign === '%' ? `${text}%` : `${sign}${text}`
}

/** Percent at the given precision, without the % sign (templates append it). */
export const formatPercentValue = (percent: number, decimals: number): string =>
  formatNumber(roundAwayFromZero(percent, decimals), 'en-US', `1.${decimals}-${decimals}`)

/**
 * Tier Progress percent with the guard: when the tier is not reached at Display Precision the percent
 * is capped one unit below 100 at the percent precision (99.99 at 2 dp, 99.9 at 1 dp).
 * `achieved` and `tier` must already be at Display Precision.
 */
export const tierPercent = (achieved: number, tier: number, percentDecimals: number): number => {
  if (tier === 0) {
    return 0
  }
  const percent = roundAwayFromZero((achieved / tier) * 100, percentDecimals)
  const cap = roundAwayFromZero(100 - 10 ** -percentDecimals, percentDecimals)
  return achieved < tier && percent > cap ? cap : percent
}

export interface ITierProgress {
  currentTier: TierType
  tier1Target: number
  tier2Target: number
  tier3Target: number
  tier1TargetFormatted: string
  tier2TargetFormatted: string
  tier3TargetFormatted: string
  current: number
  currentFormatted: string
  tier1Percent: number
  tier2Percent: number
  tier3Percent: number
  tier1PercentFormatted: string
  tier2PercentFormatted: string
  tier3PercentFormatted: string
}

/** Rounds tiers and achieved to Display Precision, then derives guarded percents and the badge. */
export const tierProgress = (
  objective: TargetType,
  tier1: number,
  tier2: number,
  tier3: number,
  achieved: number,
  percentDecimals = TARGETS_PERCENT_DECIMALS
): ITierProgress => {
  const decimals = displayDecimals(objective)
  const tier1Target = roundAwayFromZero(tier1, decimals)
  const tier2Target = roundAwayFromZero(tier2, decimals)
  const tier3Target = roundAwayFromZero(tier3, decimals)
  const current = roundAwayFromZero(achieved, decimals)

  const tier1Percent = tierPercent(current, tier1Target, percentDecimals)
  const tier2Percent = tierPercent(current, tier2Target, percentDecimals)
  const tier3Percent = tierPercent(current, tier3Target, percentDecimals)

  // Highest tier reached; zero tiers are absent, not thresholds (mirrors TierProgress.Tier on the API).
  let currentTier = TierType.Missed
  if (current !== 0) {
    currentTier =
      tier3Target > 0 && current >= tier3Target
        ? TierType.Tier3
        : tier2Target > 0 && current >= tier2Target
          ? TierType.Tier2
          : tier1Target > 0 && current >= tier1Target
            ? TierType.Tier1
            : TierType.Missed
  }

  return {
    currentTier,
    tier1Target,
    tier2Target,
    tier3Target,
    tier1TargetFormatted: formatObjectiveValue(tier1Target, objective),
    tier2TargetFormatted: formatObjectiveValue(tier2Target, objective),
    tier3TargetFormatted: formatObjectiveValue(tier3Target, objective),
    current,
    currentFormatted: formatObjectiveValue(current, objective),
    tier1Percent,
    tier2Percent,
    tier3Percent,
    tier1PercentFormatted: formatPercentValue(tier1Percent, percentDecimals),
    tier2PercentFormatted: formatPercentValue(tier2Percent, percentDecimals),
    tier3PercentFormatted: formatPercentValue(tier3Percent, percentDecimals)
  }
}
